import * as R from "ramda";
// HTTP Services
import AuthService from "services/http/endpoints/auth";
import UserService from "services/http/endpoints/user";
// Utilities
import { get } from "lodash";
import AppStore from "../state/";
import { storeToken, removeToken, readToken } from "helpers/token";
import history, { push, replace, getRedirectPath } from "helpers/history";
import useStock from "./useStock";
import showNotify from "helpers/notify";
import { getBase64 } from "helpers/index";
import GeneralService from "services/http/endpoints/general";

function useUser() {
	const { getAssetsPriceByDay, getConfigs } = useStock();

	async function login({ username, password, rememberMe }: Account.LoginInput) {
		const res = await AuthService.login({
			username,
			password,
			rememberMe,
		});

		if (!res.needOtp) {
			const token = res.token;
			storeToken(token, rememberMe);
			showNotify({ text: " ورود شما با موفقیت انجام شد", duration: 3000 });

			AppStore.userStore.loggedInStatus(true);
			AppStore.userStore.setToken(token);
			await getUserInfo();

			const redirectPath = R.pathOr(null, ["query", "redirect"])(history.location);
			if (redirectPath) {
				push(redirectPath);
				return;
			}

			push("/dashboard");
		}
	}

	async function loginConfirm({ code, mobile, rememberMe }: Account.LoginConfirm) {
		const res = await AuthService.loginConfirm({ code, mobile, rememberMe });

		const token = res.id_token;
		if (token) {
			storeToken(token, rememberMe);
			showNotify({ text: " ورود شما با موفقیت انجام شد", duration: 3000 });

			AppStore.userStore.loggedInStatus(true);
			AppStore.userStore.setToken(token);
			await getUserInfo();

			const redirectPath = R.pathOr(null, ["query", "redirect"])(history.location);
			if (redirectPath) {
				push(redirectPath);
				return;
			}

			push("/dashboard");
		}
	}

	async function aanLogin(payload: any) {
		// AAN Login
		AppStore.userStore.setAanLogin();
		const res = await AuthService.aanLogin({
			value: payload,
		});

		const token = res.id_token;
		if (token) {
			storeToken(token, true);

			AppStore.userStore.loggedInStatus(true);
			AppStore.userStore.setToken(token);
			await getUserInfo();

			replace("/dashboard");
		}
	}

	async function isUserExist(mobile: string) {
		return await AuthService.isUserExist(mobile);
	}

	async function register({ ...restData }: Account.User) {
		const res = await AuthService.register({
			...restData,
		});

		const token = res.id_token;
		if (token) {
			storeToken(token, false);

			AppStore.userStore.loggedInStatus(true);
			await getUserInfo();

			const redirectPath = R.pathOr(null, ["query", "redirect"])(history.location);
			if (redirectPath) {
				push(redirectPath);
				return;
			}

			push("/dashboard");
		}
	}

	async function registerByEmail({ ...restData }: Account.User) {
		return await AuthService.registerByEmail({
			...restData,
		});
	}

	async function finishRegisterByEmail({ ...restData }: Account.User) {
		const res = await AuthService.finishRegisterByEmail({
			...restData,
		});

		const token = res.id_token;
		if (token) {
			storeToken(token, false);

			AppStore.userStore.loggedInStatus(true);
			await getUserInfo();

			const redirectPath = R.pathOr(null, ["query", "redirect"])(history.location);
			if (redirectPath) {
				push(redirectPath);
				return;
			}

			push("/dashboard");
		}
	}

	async function verifyEmailActivationCode(code?: string) {
		return await AuthService.verifyEmailActivationCode(code);
	}

	async function verifyActivationCode({ code, mobile }: Account.VerifyActivationCode) {
		return await AuthService.verifyActivationCode({
			code,
			mobile,
		});
	}

	async function forgetPassword({ ...restData }: Account.ForgetPassword) {
		try {
			const res = await AuthService.finishForgetPassword({
				...restData,
			});

			// if reset password successful user auto logged in
			const token = res.id_token;
			if (token) {
				storeToken(token, true);

				AppStore.userStore.loggedInStatus(true);
				await getUserInfo();

				const redirectPath = R.pathOr(null, ["query", "redirect"])(history.location);
				if (redirectPath) {
					push(redirectPath);
					return;
				}

				showNotify({
					text: "رمز ورودتان تغییر پیدا کرد.",
				});
				push("/dashboard");
			}
		} catch (error) {
			//
		}
	}

	async function changePassword({ ...restData }: Account.ChangePassword) {
		await UserService.changePassword({
			...restData,
		});

		showNotify({
			text: "رمز ورودتان تغییر پیدا کرد.",
		});

		push("/dashboard");
	}

	async function updateProfile(data: Account.ProfileDetail) {
		await UserService.myUpdate(data);

		showNotify({
			text: "پروفایل شما با موفقیت ویرایش شد.",
		});

		push("/dashboard/profile");
	}

	async function myCredit() {
		// eslint-disable-next-line no-useless-catch
		try {
			// Load my gold credit
			return await UserService.dashboard();
		} catch (error) {
			throw error;
		}
	}

	async function getProfileImage(id: string) {
		const response = await GeneralService.file(id);
		return new Blob([response.data], {
			type: "image/jpeg",
		});
	}

	async function getUserInfo() {
		if (readToken() || AppStore.userStore.token) {
			const userInfo = await UserService.getUser();
			AppStore.userStore.loggedInStatus(true);
			AppStore.userStore.prepareUser(userInfo);

			const AssetsPrices = await getAssetsPriceByDay(60);
			// @ts-ignore
			AppStore.userStore.setAssetsPrices(AssetsPrices);

			const configs = await getConfigs();
			AppStore.userStore.setBaazarConfigs(configs);

			const credits = await myCredit();
			AppStore.userStore.setAccountCredits(credits);

			if (get(userInfo, ["profile", "profileImage", "id"])) {
				try {
					const profileImage = await getProfileImage(get(userInfo, ["profile", "profileImage", "id"]));
					getBase64(profileImage, (img: any) => {
						AppStore.userStore.setProfileImage(img);
					});
				} catch (error) {
					console.log(error);
				}
			}

			AppStore.userStore.setLoading(false);
		}
	}

	async function getAssetsInfo() {
		AppStore.userStore.setLoading(true);

		const AssetsPrices = await getAssetsPriceByDay(60);
		// @ts-ignore
		AppStore.userStore.setAssetsPrices(AssetsPrices);

		const credits = await myCredit();
		AppStore.userStore.setAccountCredits(credits);
		AppStore.userStore.setLoading(false);
	}

	/**
	 * Logout the user from the application
	 * Steps:
	 * 1) Remove user token
	 * 2) Reset the user into the User store
	 * 3) Redirect user to login page
	 */
	async function logout() {
		removeToken();
		AppStore.userStore.resetUser();

		push(`/auth/login?redirect=${getRedirectPath()}`);
	}

	return {
		login,
		loginConfirm,
		aanLogin,
		register,
		registerByEmail,
		finishRegisterByEmail,
		verifyEmailActivationCode,
		isUserExist,
		verifyActivationCode,
		logout,
		getUserInfo,
		updateProfile,
		changePassword,
		forgetPassword,
		getAssetsInfo,
	};
}

export default useUser;
